import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import { useCallback } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/writeFragmentInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isExpectedError } from '../../utils/isExpectedError.ts';
import { type OnboardingKeysCombinedType } from '../../constants/onboarding-keys-combined.ts';

import onboardingStateGetOnboardingStateQuery, {
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import type {
	OnboardingStateInput,
	onboardingStateSetOnboardingStateMutation as onboardingStateSetOnboardingStateMutation$data,
	onboardingStateSetOnboardingStateMutationVariables as onboardingStateSetOnboardingStateMutation$variables,
} from './__types__/onboardingStateSetOnboardingStateMutation.ts';

// PersonalizationContract fragment baseline. Colocated here so the onboarding key/value pair is
// owned by its consumer, then spread by whichever operation supplies it.
//
// Unlike the other field groups in this contract, this fragment is NOT spread into the legacy
// operation: `onboardingStateGetOnboardingStateQuery` has no source `graphql` tag anywhere in
// confluence/ — only a checked-in generated artifact that 48 files import directly — so there is no
// operation source to add a spread to. Its first spread is therefore in
// ViewPagePersonalizationQuery. See confluence/openspec/changes/personalization-contract.
export const OnboardingStateFragment: GraphQLTaggedNode = graphql`
	fragment onboardingState_state on OnboardingState {
		key
		value
	}
`;

// Relay generated types are immutable (readonly) whereas Apollo expects mutable types
type OnboardingStateData = {
	onboardingState?:
		| readonly {
				key: string;
				value: string | null | undefined;
		  }[]
		| null;
};

export const deserializeState = (
	data: onboardingStateGetOnboardingStateQuery$data | OnboardingStateData | null | undefined,
): any => {
	const state = {} as any;
	// new graphQL call returns onboardingState array
	const { onboardingState } = data || {};

	// parse data and populate state accordingly
	if (onboardingState !== undefined && onboardingState !== null) {
		for (const { key, value } of onboardingState) {
			// key comes back as "{prefix}:{property}" format
			// split by ':' to get parsed {property}
			// some keys don't have a prefix, which is why we have the ||
			const parsedKey = key.split(':')[1] || key;
			if (!value) {
				continue;
			}
			if (/(true|false)/g.test(value)) {
				state[parsedKey] = value === 'true';
			} else {
				state[parsedKey] = value;
			}
		}
	}
	return state;
};

export const useSetOnboardingState = (): {
	setOnboardingState: (newOnboardingState: OnboardingStateInput | OnboardingStateInput[]) => void;
	error: ApolloError | undefined;
	called: boolean;
} => {
	const [setState, { error, called }] = useMutationInterop<
		onboardingStateSetOnboardingStateMutation$data,
		onboardingStateSetOnboardingStateMutation$variables
	>(
		graphql`
			mutation onboardingStateSetOnboardingStateMutation($states: [OnboardingStateInput!]!) {
				setOnboardingState(states: $states) {
					key
					value
				}
			}
		`,
		{
			update(_cache, { data }) {
				const states = data!.setOnboardingState;
				(states ?? []).forEach(({ key, value }) => {
					// writeFragment can only handle a single data item at a time
					writeFragmentInterop({
						fragmentId: `OnboardingState:${key}`,
						data: {
							key,
							value: value ?? null,
							__typename: 'OnboardingState',
						},
					});
				});
			},
			onError: (error) => {
				if (isExpectedError(error)) {
					markErrorAsHandled(error);
				}
			},
		},
	);

	type SetOnboardingStateArgs = OnboardingStateInput | OnboardingStateInput[];

	const setOnboardingState = useCallback(
		(newOnboardingState: SetOnboardingStateArgs): void => {
			let states: OnboardingStateInput[];
			if (Array.isArray(newOnboardingState)) {
				// supports batch updates
				states = newOnboardingState;
			} else {
				// backwards compability for single key-value pair
				states = [newOnboardingState];
			}

			void setState({
				variables: {
					states,
				},
				optimisticResponse: {
					setOnboardingState: states.map(({ key, value }) => ({
						key,
						value,
						__typename: 'OnboardingState',
					})),
				},
			});
		},
		[setState],
	);

	return { setOnboardingState, error, called };
};

// safeguards against empty arrays or strings
const hasValidKeysToFetch = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
): boolean => {
	if (Array.isArray(key)) return key.length > 0;
	if (typeof key === 'string') return key.length > 0;
	return false;
};

/**
 * TODO: Migrate to fetch from userPreferences - this is considered deprecated
 *
 * @param key - The onboarding state key(s) to fetch
 * @param skip - Whether to skip the query
 */
export const useGetOnboardingState = (
	key: OnboardingKeysCombinedType | OnboardingKeysCombinedType[],
	skip = false,
): {
	data: onboardingStateGetOnboardingStateQuery$data | undefined;
	error: ApolloErrorInterop | undefined;
	loading: boolean;
	refetch: RefetchSupportedType<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>;
} => {
	const { isLicensed, isGuest } = useAccessStatus();
	const isAllowed = isLicensed || isGuest;

	const hasKeysToFetch = hasValidKeysToFetch(key);
	const skipQuery = skip || !isAllowed || !hasKeysToFetch;

	const { data, error, loading, refetch } = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: key as unknown as string[] },
		skip: skipQuery,
	});

	return {
		data,
		error,
		loading,
		refetch,
	};
};
